import { useEffect, useLayoutEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { VistaBarbearia } from '../../utils/barbeariaTipos';
import {
  TOUR_PASSOS,
  marcarTourConcluido,
  type TourStep,
} from '../../utils/barbeariaTour';

interface Props {
  ativo: boolean;
  vista: VistaBarbearia;
  onMudarVista: (v: VistaBarbearia) => void;
  onFechar: () => void;
  passoInicial?: number;
}

/** Coordenadas no viewport (position: fixed). */
type Box = { top: number; left: number; width: number; height: number };

function medirViewport(target: string): Box | null {
  const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export default function BarbeariaTour({
  ativo,
  vista,
  onMudarVista,
  onFechar,
  passoInicial = 0,
}: Props) {
  const [idx, setIdx] = useState(passoInicial);
  const [box, setBox] = useState<Box | null>(null);
  const passo: TourStep | undefined = TOUR_PASSOS[idx];

  useEffect(() => {
    if (!ativo) return;
    setIdx(passoInicial);
  }, [ativo, passoInicial]);

  useEffect(() => {
    if (!ativo || !passo) return;
    if (passo.vista !== vista) onMudarVista(passo.vista);
  }, [ativo, passo, vista, onMudarVista]);

  useLayoutEffect(() => {
    if (!ativo || !passo) return;
    let tentativas = 0;
    let timer: number | undefined;

    const atualizar = () => {
      const el = document.querySelector(`[data-tour="${passo.target}"]`) as HTMLElement | null;
      if (el) {
        // Deixa espaço embaixo para o balão fixo (~200px)
        const margemBaixo = 220;
        const r = el.getBoundingClientRect();
        if (r.bottom > window.innerHeight - margemBaixo || r.top < 56) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
      const m = medirViewport(passo.target);
      setBox(m);
      if (!m && tentativas < 15) {
        tentativas += 1;
        timer = window.setTimeout(atualizar, 90);
      }
    };

    timer = window.setTimeout(atualizar, 80);
    const onMove = () => setBox(medirViewport(passo.target));
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [ativo, passo, vista, idx]);

  if (!ativo || !passo) return null;

  const pad = 3;
  const maxH = Math.round(window.innerHeight * 0.38);
  const maxW = Math.round(window.innerWidth * 0.94);

  let highlight: Box | null = null;
  if (box) {
    const width = Math.min(box.width + pad * 2, maxW);
    const height = Math.min(box.height + pad * 2, maxH);
    let left = box.left - pad;
    let top = box.top - pad;
    // mantém dentro da tela
    left = Math.max(6, Math.min(left, window.innerWidth - width - 6));
    top = Math.max(6, Math.min(top, window.innerHeight - height - 210));
    highlight = { top, left, width, height };
  }

  const fechar = (concluir: boolean) => {
    if (concluir) marcarTourConcluido();
    onFechar();
  };

  const proximo = () => {
    if (idx >= TOUR_PASSOS.length - 1) {
      fechar(true);
      return;
    }
    setIdx((i) => i + 1);
  };

  const anterior = () => {
    if (idx <= 0) return;
    setIdx((i) => i - 1);
  };

  return (
    <div className="pointer-events-none fixed inset-0 z-[200]" aria-live="polite">
      {/* overlay: highlight em coordenadas de viewport */}
      <div className="pointer-events-auto absolute inset-0">
        {highlight ? (
          <div
            className="fixed rounded-xl border-2 border-amber-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] ring-2 ring-amber-300/60 transition-all duration-150"
            style={{
              top: highlight.top,
              left: highlight.left,
              width: highlight.width,
              height: highlight.height,
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-black/55" />
        )}
      </div>

      {/* Balão SEMPRE fixo embaixo — botões nunca saem da tela */}
      <div
        className="pointer-events-auto fixed bottom-3 left-1/2 z-[210] w-[min(24rem,calc(100vw-1.25rem))] -translate-x-1/2 rounded-2xl border-2 border-amber-800 bg-white p-3.5 shadow-2xl"
        role="dialog"
        aria-label="Tutorial"
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
              Tutorial {idx + 1}/{TOUR_PASSOS.length}
            </p>
            <h3 className="text-sm font-bold text-neutral-900 sm:text-base">{passo.title}</h3>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg p-1 text-neutral-500 hover:bg-neutral-100"
            onClick={() => fechar(true)}
            aria-label="Fechar tutorial"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="max-h-24 overflow-y-auto text-sm leading-relaxed text-neutral-700">
          {passo.body}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="text-xs font-semibold text-neutral-500 hover:text-neutral-800"
            onClick={() => fechar(true)}
          >
            Pular
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={idx === 0}
              onClick={anterior}
              className="rounded-lg border-2 border-neutral-400 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={proximo}
              className="rounded-lg border-2 border-amber-900 bg-amber-600 px-3 py-1.5 text-xs font-bold text-white"
            >
              {idx >= TOUR_PASSOS.length - 1 ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
